import React, { useMemo, useState } from 'react';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Select,
  Empty,
  Spin,
  Alert,
  Typography,
  Space,
  Segmented,
  Divider,
  Statistic,
  Tag,
  Dropdown,
  Button,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { formatDateOnly } from '../../utils/dateHelpers';
import {
  useGetDailyMenusByDateQuery,
  useGetDailyMenuCountsRangeQuery,
} from '../../services/dailyMenuApi';
import { DailyMenuCountsPanel } from './DailyMenuCountsPanel';
import { MEAL_TYPE_OPTIONS, MEAL_TYPE_LABELS } from './mealTypeConstants';
import type { MealType, DailyMenuRangeCountsReport } from '../../types/dailyMenu';
import { useAppSelector } from '../../hooks/redux';
import {
  exportCSV,
  exportExcel,
  exportPDF,
  ExportConfig,
} from '../../utils/reportExport';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

type Mode = 'day' | 'range';

export const DailyMenuCountsReport: React.FC = () => {
  const [mode, setMode] = useState<Mode>('day');

  // Day mode
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [mealType, setMealType] = useState<MealType>('Lunch');

  // Range mode
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(7, 'day'),
    dayjs(),
  ]);
  const [rangeMealType, setRangeMealType] = useState<MealType | 'All'>('Lunch');

  return (
    <div>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Segmented
            value={mode}
            onChange={(v) => setMode(v as Mode)}
            options={[
              { label: 'Por fecha', value: 'day' },
              { label: 'Agregado (rango)', value: 'range' },
            ]}
          />

          {mode === 'day' ? (
            <Space wrap size="middle" align="center">
              <div>
                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                  Fecha
                </Text>
                <DatePicker
                  value={date}
                  onChange={(d) => d && setDate(d)}
                  format="DD/MM/YYYY"
                  allowClear={false}
                />
              </div>
              <div>
                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                  Tipo de comida
                </Text>
                <Select
                  value={mealType}
                  onChange={setMealType}
                  options={MEAL_TYPE_OPTIONS}
                  style={{ width: 140 }}
                />
              </div>
            </Space>
          ) : (
            <Space wrap size="middle" align="center">
              <div>
                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                  Rango de fechas
                </Text>
                <RangePicker
                  value={range}
                  onChange={(v) => {
                    if (v && v[0] && v[1]) setRange([v[0], v[1]]);
                  }}
                  format="DD/MM/YYYY"
                  allowClear={false}
                />
              </div>
              <div>
                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                  Tipo de comida
                </Text>
                <Select
                  value={rangeMealType}
                  onChange={setRangeMealType}
                  style={{ width: 160 }}
                  options={[
                    { label: 'Todos', value: 'All' },
                    ...MEAL_TYPE_OPTIONS,
                  ]}
                />
              </div>
            </Space>
          )}
        </Space>
      </Card>

      {mode === 'day' ? (
        <DayModePanel date={date} mealType={mealType} />
      ) : (
        <RangeModePanel
          startDate={range[0]}
          endDate={range[1]}
          mealType={rangeMealType}
        />
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------

interface DayModeProps {
  date: Dayjs;
  mealType: MealType;
}

const DayModePanel: React.FC<DayModeProps> = ({ date, mealType }) => {
  const dateParam = useMemo(() => date.format('YYYY-MM-DD'), [date]);

  const { data: menus, isFetching, isError } = useGetDailyMenusByDateQuery({
    date: dateParam,
    mealType,
  });

  if (isFetching) {
    return (
      <div style={{ textAlign: 'center', padding: 24 }}>
        <Spin />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert
        type="error"
        showIcon
        message="No se pudieron cargar los menús de la fecha seleccionada"
      />
    );
  }

  if (!menus || menus.length === 0) {
    return (
      <Empty description="No hay menú del día configurado para esta fecha y tipo de comida" />
    );
  }

  return (
    <Row gutter={[16, 16]}>
      {menus.map((menu) => (
        <Col key={menu.id} xs={24} lg={menus.length > 1 ? 12 : 24}>
          <Card
            size="small"
            title={
              <Title level={5} style={{ margin: 0 }}>
                {menu.name}
              </Title>
            }
          >
            <DailyMenuCountsPanel menuId={menu.id} bordered={false} showHeader={false} exportable />
          </Card>
        </Col>
      ))}
    </Row>
  );
};

// -----------------------------------------------------------------------------

interface RangeModeProps {
  startDate: Dayjs;
  endDate: Dayjs;
  mealType: MealType | 'All';
}

function buildRangeExportConfig(
  data: DailyMenuRangeCountsReport,
  restaurantName: string,
  timestamp: string,
): ExportConfig {
  const start = formatDateOnly(data.startDate, 'DD/MM/YYYY');
  const end = formatDateOnly(data.endDate, 'DD/MM/YYYY');
  const mealLabel = data.mealType ? MEAL_TYPE_LABELS[data.mealType] : 'Todos';

  return {
    reportTitle: 'Menú del Día — Agregado',
    periodLabel: `${start} - ${end} (${mealLabel})`,
    restaurantName,
    timestamp,
    summaryCards: [
      { label: 'Total comensales', value: `${data.totalDinersOrdered}` },
      { label: 'Días con datos', value: `${data.daysWithData}` },
      { label: 'Tipo de comida', value: mealLabel },
    ],
    sections: data.courses.map((course) => ({
      title: course.courseName,
      titleStyle: 'section' as const,
      headers: ['Opción', 'Cantidad'],
      rows: course.options.map((o) => [o.optionName, o.count]),
    })),
  };
}

const RangeModePanel: React.FC<RangeModeProps> = ({ startDate, endDate, mealType }) => {
  const { data, isFetching, isError } = useGetDailyMenuCountsRangeQuery({
    startDate: startDate.format('YYYY-MM-DD'),
    endDate: endDate.format('YYYY-MM-DD'),
    mealType: mealType === 'All' ? undefined : mealType,
  });

  const { currentRestaurant } = useAppSelector((state) => state.restaurant);
  const { formatInTimeZone } = useRestaurantTimeZone();

  const handleExport = (format: 'csv' | 'excel' | 'pdf') => {
    if (!data) return;
    const config = buildRangeExportConfig(
      data,
      currentRestaurant?.name || 'N/A',
      formatInTimeZone(new Date(), 'DD/MM/YYYY HH:mm'),
    );
    const prefix = `menu-del-dia-agregado-${formatDateOnly(data.startDate, 'YYYYMMDD')}-${formatDateOnly(data.endDate, 'YYYYMMDD')}`;
    if (format === 'csv') exportCSV(config, prefix);
    else if (format === 'excel') exportExcel(config, prefix);
    else exportPDF(config);
  };

  if (isFetching) {
    return (
      <div style={{ textAlign: 'center', padding: 24 }}>
        <Spin />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert
        type="error"
        showIcon
        message="No se pudo cargar el reporte agregado"
      />
    );
  }

  if (!data || data.courses.length === 0) {
    return (
      <Empty description="No hay datos para el rango y tipo de comida seleccionados" />
    );
  }

  return (
    <Card
      size="small"
      extra={
        <Dropdown
          menu={{
            items: [
              { key: 'csv', label: 'CSV', onClick: () => handleExport('csv') },
              { key: 'excel', label: 'Excel', onClick: () => handleExport('excel') },
              { key: 'pdf', label: 'PDF', onClick: () => handleExport('pdf') },
            ],
          }}
        >
          <Button size="small" icon={<DownloadOutlined />}>
            Exportar
          </Button>
        </Dropdown>
      }
      title={<Text strong>Reporte agregado</Text>}
    >
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8}>
          <Statistic title="Total comensales" value={data.totalDinersOrdered} />
        </Col>
        <Col xs={12} sm={8}>
          <Statistic title="Días con datos" value={data.daysWithData} />
        </Col>
        <Col xs={24} sm={8}>
          <Text type="secondary" style={{ display: 'block' }}>
            {formatDateOnly(data.startDate, 'DD MMM YYYY')} —{' '}
            {formatDateOnly(data.endDate, 'DD MMM YYYY')}
          </Text>
          {data.mealType && (
            <Tag color="blue" style={{ marginTop: 4 }}>
              {MEAL_TYPE_LABELS[data.mealType]}
            </Tag>
          )}
          {!data.mealType && (
            <Tag color="default" style={{ marginTop: 4 }}>
              Todos
            </Tag>
          )}
        </Col>
      </Row>

      <Divider style={{ margin: '8px 0 16px' }} />

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {data.courses.map((course) => (
          <div key={course.courseName}>
            <Row justify="space-between" align="middle">
              <Col>
                <Text strong>{course.courseName}</Text>
              </Col>
              <Col>
                <Text type="secondary">Total: {course.totalCount}</Text>
              </Col>
            </Row>
            <Divider style={{ margin: '6px 0' }} />
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              {course.options.map((opt) => (
                <Row
                  key={`${course.courseName}-${opt.optionName}`}
                  justify="space-between"
                >
                  <Col flex="auto">
                    <Text style={{ fontSize: 14 }}>{opt.optionName}</Text>
                  </Col>
                  <Col>
                    <Text strong style={{ fontSize: 14 }}>
                      {opt.count}
                    </Text>
                  </Col>
                </Row>
              ))}
            </Space>
          </div>
        ))}
      </Space>
    </Card>
  );
};

export default DailyMenuCountsReport;
